import {Category, CategoryPrice} from '@/apiTypes/event';
import {Typography} from '@/components/ui/typography';
import {cn} from '@/lib/utils';
import {formatPrice} from '@/utils/currencyUtils';
import {formatTime} from '@/utils/displayFormatUtils';
import {Clock} from 'lucide-react';
import {FC} from 'react';

/**
 * Returns the number of spots left in a category, or undefined when it can't
 * be determined. Prefers the scraped `spotsRemaining` (bikereg events) and
 * otherwise derives it from `maxParticipants - participantCount`. When
 * `maxParticipants` is missing there is no capacity to count against, so the
 * result is undefined.
 */
export const getSpotsRemaining = (category: Category): number | undefined => {
  if (category.spotsRemaining) return category.spotsRemaining;
  if (category.maxParticipants == null) return undefined;
  return category.maxParticipants - (category.participantCount ?? 0);
};

/**
 * Returns the currently-active price tier for a category. A tier is "current"
 * when its `endsAt` is in the future (or null, meaning no expiry). When
 * multiple tiers qualify, the one ending soonest wins — that's the price
 * you'd pay if you registered right now.
 */
export const getCurrentPrice = (
  category: Category
): CategoryPrice | undefined => {
  const now = Date.now();
  return (category.prices ?? [])
    .filter((p) => !p.endsAt || new Date(p.endsAt).getTime() > now)
    .sort((a, b) => {
      if (!a.endsAt) return 1;
      if (!b.endsAt) return -1;
      return new Date(a.endsAt).getTime() - new Date(b.endsAt).getTime();
    })[0];
};

/**
 * Returns the next price tier that will take over after the current one ends,
 * along with the date it starts (which is the current tier's `endsAt`).
 * Returns undefined when the current tier has no `endsAt` (it's the final
 * tier) or when no subsequent tier exists.
 */
export const getNextPrice = (
  category: Category
): {price: CategoryPrice; startsAt: Date} | undefined => {
  const current = getCurrentPrice(category);
  if (!current?.endsAt) return undefined;

  const currentEnd = new Date(current.endsAt).getTime();
  const next = (category.prices ?? [])
    .filter((p) => {
      if (p.id === current.id) return false;
      if (!p.endsAt) return true;
      return new Date(p.endsAt).getTime() > currentEnd;
    })
    .sort((a, b) => {
      if (!a.endsAt) return 1;
      if (!b.endsAt) return -1;
      return new Date(a.endsAt).getTime() - new Date(b.endsAt).getTime();
    })[0];

  if (!next) return undefined;
  return {price: next, startsAt: new Date(current.endsAt)};
};

interface CategoryItemProps {
  category: Category;
}
export const CategoryItem: FC<CategoryItemProps> = ({category}) => {
  const currentPrice = getCurrentPrice(category);
  const nextPrice = getNextPrice(category);
  const isOpen = category.isOpen ?? true;
  const isSoldOut = category.isSoldOut ?? false;
  const spotsRemaining = getSpotsRemaining(category);
  const showSpots = !isSoldOut && isOpen && !!spotsRemaining;
  const showMetaRow = category.startTime || isSoldOut || showSpots;

  return (
    <div className={`flex flex-1 justify-between ${isSoldOut && 'opacity-50'}`}>
      <div className="flex flex-col gap-1 items-start">
        <Typography
          level="B2"
          className={isSoldOut ? 'text-neutral-500' : 'text-neutral-900'}
        >
          {category.name}
        </Typography>
        {!isOpen && !isSoldOut && (
          <Typography level="B3" className="text-neuytral-600">
            Closed
          </Typography>
        )}
        {category.description && (
          <Typography level="B4" className="text-neutral-500">
            {category.description}
          </Typography>
        )}
        {showMetaRow && (
          <div className="flex gap-2 items-center">
            {category.startTime && (
              <div className="flex gap-1 items-center">
                <Clock className="w-3 h-3 inline text-neutral-600" />
                <Typography level="C1" className="text-neutral-600">
                  {formatTime(category.startTime)}
                </Typography>
              </div>
            )}
            {category.startTime && (isSoldOut || showSpots) && (
              <Typography level="C1" className="text-neutral-400">
                ·
              </Typography>
            )}
            {isSoldOut && (
              <Typography
                level="C1"
                className="text-error-700 font-medium text-nowrap"
              >
                Sold Out
              </Typography>
            )}
            {showSpots && spotsRemaining && (
              <Typography
                level="C1"
                className={cn(
                  'text-nowrap',
                  spotsRemaining <= 10
                    ? 'text-warning-600 font-medium'
                    : 'text-neutral-400'
                )}
              >
                {spotsRemaining} spots left
              </Typography>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 items-end justify-between">
        <div className="flex flex-col items-end">
          {currentPrice && (
            <Typography
              level="B2"
              className={isSoldOut ? 'text-neutral-500' : 'text-neutral-900'}
            >
              {formatPrice(currentPrice.amount)}
            </Typography>
          )}
          {nextPrice && (
            <Typography level="C1" className="text-error-400 text-nowrap">
              {formatPrice(nextPrice.price.amount)} after{' '}
              {nextPrice.startsAt.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};
